import { useTranslation } from 'react-i18next';
import { AccountWorkflowStatus, FieldData, Select, TextField, useForm } from 'vopay';
import { Checkbox } from 'UI';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import type { PartnerAccountStatusFilter } from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';

/** Field names double as the inputs' `name`s (Cypress selects on `select-Status-trigger` etc.). */
export type AccountFilterValues = {
  Country: string;
  Currency: string;
  Status: PartnerAccountStatusFilter;
  SearchText: string;
  /** The chart view's checkbox (ID-14095): a field only for its place in the row; its value is the host's, never Formik's. */
  ShowPartnerAccounts: false;
};

const CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP'];

/** The chart view's own filter state (ID-14095): the Show Partner Accounts checkbox. Absent in table view. */
export type AccountChartViewFilter = {
  /** False for a partner with no partner accounts to add: the checkbox is left out. */
  canShowPartnerAccounts: boolean;
  isShowingPartnerAccounts: boolean;
  onShowPartnerAccountsChange: (isOn: boolean) => void;
};

type UseAccountFilterFormConfigProps = {
  /** Comes from the URL, so the form re-seeds whenever the query string changes. */
  initialValues: Omit<AccountFilterValues, 'ShowPartnerAccounts'>;
  /** The statuses to offer, in order: the overview's stage tiles (ID-13964 D3). */
  statuses: AccountWorkflowStatus[];
  chartView?: AccountChartViewFilter;
  onSubmit: (values: AccountFilterValues) => void;
};

export default function useAccountFilterFormConfig({
  initialValues,
  statuses,
  chartView,
  onSubmit,
}: UseAccountFilterFormConfigProps) {
  const config = useConfig(statuses, chartView);

  const { ctx, renderFormElements } = useForm<AccountFilterValues>({
    // A constant, not the checkbox's state: the form re-seeds whenever these change, which would
    // throw away a keyword typed but not yet searched for every time the box is ticked.
    initialValues: { ...initialValues, ShowPartnerAccounts: false },
    config,
    onSubmit,
    enableReinitialize: true,
  });

  return { ctx, renderFormElements: () => renderFormElements({ config }) };
}

function useConfig(
  statuses: AccountWorkflowStatus[],
  chartView: AccountChartViewFilter | undefined,
): FieldData<AccountFilterValues>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.List.SearchForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();

  const allOption = { label: t('allOption'), value: 'all' };

  // Raw API country names stay the option values (they are what the request filters on); only the
  // labels are localized.
  const countryOptions = [
    allOption,
    { label: tGlobal('Countries.CA'), value: 'Canada' },
    { label: tGlobal('Countries.USLong'), value: 'United States' },
  ];

  const currencyOptions = [allOption, ...CURRENCIES.map((currency) => ({ label: currency, value: currency }))];

  // The same statuses, in the same order, as the overview's stage bar (ID-13964 D3); the label
  // resolver keeps the raw value as the key.
  const statusOptions = [
    allOption,
    ...statuses.map((status) => ({ label: resolveStatusLabel(status), value: status })),
  ];

  return [
    {
      fieldName: 'Country',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<AccountFilterValues['Country']>({})}
          placeholder={t('countryPlaceholder')}
          options={countryOptions}
          sort={false}
        />
      ),
    },
    {
      fieldName: 'Currency',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<AccountFilterValues['Currency']>({})}
          placeholder={t('currencyPlaceholder')}
          options={currencyOptions}
          sort={false}
        />
      ),
    },
    {
      fieldName: 'Status',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<AccountFilterValues['Status']>({})}
          placeholder={t('statusPlaceholder')}
          options={statusOptions}
          sort={false}
        />
      ),
    },
    {
      fieldName: 'ShowPartnerAccounts',
      getInputState: () => 'optional',
      showField: () => chartView?.canShowPartnerAccounts === true,
      // Figma `Check option` (the design-system `Selectors` box, then its label), in the filter row.
      // Ticking it redraws the chart at once, so the value is the host's rather than Formik's.
      renderField: () => (
        <Checkbox
          name="ShowPartnerAccounts"
          label={t('showPartnerAccounts', { defaultValue: 'Show Partner Accounts' })}
          checked={chartView?.isShowingPartnerAccounts ?? false}
          onChange={(isOn) => chartView?.onShowPartnerAccountsChange(isOn)}
        />
      ),
    },
    {
      fieldName: 'SearchText',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <TextField
          {...getFormikData<AccountFilterValues['SearchText']>({})}
          placeholder={t('keywordPlaceholder')}
          variant="outlineShadow"
        />
      ),
    },
  ];
}
